import type { UseElementVisibilityReturn } from '@vueuse/core';
import { createSharedComposable, useElementBounding, useElementVisibility } from '@vueuse/core';
import { type Directive, reactive, toValue } from 'vue';
import { computed } from 'vue';

interface TargetVisibility {
  el: HTMLElement;
  isVisible: UseElementVisibilityReturn<false>;
  boundingClientRect: ReturnType<typeof useElementBounding>;
}

export const useVisibleAnchor = createSharedComposable(() => {
  const anchorVisibility = reactive<Record<string, TargetVisibility>>({});

  const vTrackAnchorTarget: Directive = {
    mounted(el) {
      if (!el.id) return;

      anchorVisibility[el.id] = {
        el,
        isVisible: useElementVisibility(el),
        boundingClientRect: useElementBounding(el),
      };
    },
    unmounted(el) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete anchorVisibility[el.id];
    },
  };

  const isAnchorTargetVisible = (anchor: string) => {
    return anchorVisibility[anchor]?.isVisible ?? false;
  };

  const activeAnchorTarget = computed(() => {
    let activeTarget;
    for (const anchorId in anchorVisibility) {
      const anchor = anchorVisibility[anchorId];
      if (!toValue(anchor.isVisible)) continue;

      if (!activeTarget
        || Math.abs(toValue(anchor.boundingClientRect.top)) < Math.abs(toValue(activeTarget.boundingClientRect.top))) {
        activeTarget = anchorVisibility[anchorId];
      }
    }
    return activeTarget?.el;
  });

  return {
    vTrackAnchorTarget,
    isAnchorTargetVisible,
    activeAnchorTarget,
  };
});
